import React, { useEffect, useRef, useState } from "react";
import { ConstructorEventUpdateSQLStatus } from "../../../../Event/event";

const categoriesInitial = [
  { color: "#e74c3c", defaultLabel: "Apples", defaultCount: 5 },
  { color: "#f1c40f", defaultLabel: "Bananas", defaultCount: 3 },
  { color: "#e67e22", defaultLabel: "Oranges", defaultCount: 7 },
  { color: "#8e44ad", defaultLabel: "Grapes", defaultCount: 2 },
];

export default function PieChartBuilder() {
  const canvasRef = useRef(null);

  // State for labels and counts (controlled inputs)
  const [categories, setCategories] = useState(
    categoriesInitial.map((cat) => ({
      label: cat.defaultLabel,
      count: cat.defaultCount,
      color: cat.color,
    }))
  );

  // Draw pie chart whenever categories state changes
  useEffect(() => {
    drawPieChart();
  }, [categories]);

  function drawPieChart() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;

    const values = categories.map((c) => (c.count >= 0 ? c.count : 0));
    const labels = categories.map((c) =>
      c.label.trim().length ? c.label.trim() : "Unnamed"
    );

    const total = values.reduce((sum, v) => sum + v, 0);

    ctx.clearRect(0, 0, width, height);

    if (total === 0) {
      ctx.fillStyle = "#555";
      ctx.font = "20px Arial";
      ctx.textAlign = "center";
      ctx.fillText(
        "Please enter values greater than 0",
        width / 2,
        height / 2
      );
      return;
    }

    let startAngle = -0.5 * Math.PI; // start from top

    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(cx, cy) * 0.8;

    values.forEach((value, i) => {
      if (value <= 0) return;

      const sliceAngle = (value / total) * 2 * Math.PI;

      // Draw slice
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, startAngle, startAngle + sliceAngle);
      ctx.closePath();
      ctx.fillStyle = categories[i].color;
      ctx.fill();

      // Draw slice border
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw label & percentage
      const midAngle = startAngle + sliceAngle / 2;
      const labelX = cx + Math.cos(midAngle) * radius * 0.6;
      const labelY = cy + Math.sin(midAngle) * radius * 0.6;

      const percent = ((value / total) * 100).toFixed(1) + "%";

      ctx.fillStyle = "#fff";
      ctx.font = "bold 14px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillText(labels[i], labelX, labelY - 10);
      ctx.fillText(percent, labelX, labelY + 10);

      startAngle += sliceAngle;
    });
  }

  function handleLabelChange(index, newLabel) {
    setCategories((cats) => {
      const updated = [...cats];
      updated[index] = { ...updated[index], label: newLabel };
      return updated;
    });
  }

  function handleCountChange(index, newCount) {
    const parsed = parseInt(newCount, 10);
    setCategories((cats) => {
      const updated = [...cats];
      updated[index] = {
        ...updated[index],
        count: isNaN(parsed) || parsed < 0 ? 0 : parsed,
      };
      return updated;
    });
  }

  function downloadChart() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "my-pie-chart.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
       const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
  }

  return (
    
    <div
      style={{
        fontFamily:
          "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        maxWidth: 600,
        margin: "20px auto",
        padding: "0 20px",
        textAlign: "center",
      }}
    >
      <h3 style={{ color: "#2c3e50" }}>Build Your Own Pie Chart</h3>
      <p>Enter counts and labels for each fruit below to build your pie chart.</p>

      <div
        style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-around",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        {categories.map((cat, i) => (
          <div
            key={i}
            style={{
              margin: 10,
              flex: "0 0 140px",
              maxWidth: 160,
              minWidth: 140,
              boxSizing: "border-box",
            }}
          >
            <label
              htmlFor={`label-${i}`}
              style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}
            >
              Label
            </label>
            <input
              type="text"
              id={`label-${i}`}
              maxLength={20}
              value={cat.label}
              onChange={(e) => handleLabelChange(i, e.target.value)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: 4,
                fontSize: "1em",
                textAlign: "center",
                marginBottom: 8,
              }}
            />
            <label
              htmlFor={`count-${i}`}
              style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}
            >
              Count
            </label>
            <input
              type="number"
              id={`count-${i}`}
              min={0}
              value={cat.count}
              onChange={(e) => handleCountChange(i, e.target.value)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: 4,
                fontSize: "1em",
                textAlign: "center",
              }}
            />
          </div>
        ))}
      </div>

      <canvas
        ref={canvasRef}
        width={400}
        height={400}
        style={{
          maxWidth: "100%",
          margin: "0 auto 20px",
          border: "1px solid #ccc",
          borderRadius: 8,
          display: "block",
        }}
      />
      <button
        onClick={downloadChart}
        style={{
          backgroundColor: "#2980b9",
          color: "white",
          border: "none",
          padding: "10px 16px",
          fontSize: "1em",
          borderRadius: 6,
          cursor: "pointer",
          transition: "background-color 0.3s ease",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#1c5980")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#2980b9")
        }
      >
        Download Pie Chart
      </button>
    </div>
  );
}
